/**
 * The company's colour, as nine CSS variables — `docs/proposal.md` §5.3.
 *
 * `brandPalette()` in `shared/src/brand-palette.ts` is the one thing that
 * decides what a saved hex may be used for; this is the one thing that puts
 * its answers on the page. Everything inside `client/src/quote/proposal/`
 * then reads `bg-brand`, `text-brand-tx`, `bg-brand-t1` and friends — the
 * Tailwind names wired to these variables in `client/tailwind.config.ts` —
 * and **never a hex and never `brandPalette` itself**, which is what makes
 * §13's "no `#` colours in the proposal module" a thing a reviewer can check
 * by searching one directory.
 */
import type { CSSProperties } from 'react'
import { brandPalette, DEFAULT_BRAND, type BrandPalette, type ProposalIdentity } from '@rowted/shared'

export interface BrandSkin {
  palette: BrandPalette
  /** For the root element's `style`. */
  vars: CSSProperties
  /**
   * Whether the cover is a band of the brand colour.
   *
   * The company's own choice, **and** never when the colour is near white:
   * a white band on a white page is not a cover, so board 6's "Copperline"
   * white cover with a 6px rule is what that becomes.
   */
  band: boolean
}

export function brandSkin(identity: ProposalIdentity): BrandSkin {
  const palette = brandPalette(identity.brandColor ?? DEFAULT_BRAND)
  return {
    palette,
    vars: {
      '--b': palette.fill,
      '--on': palette.on,
      '--tx': palette.text,
      '--ui': palette.ui,
      '--t1': palette.tint1,
      '--t2': palette.tint2,
      '--ln': palette.line,
      '--ring': palette.ring,
      '--contour': palette.contour,
    } as CSSProperties,
    band: identity.band && !palette.nearWhite,
  }
}

/**
 * The letter the cover tile shows when no logo was uploaded (§5.1).
 *
 * Never the Rowted mark: the document is the company's, and a proposal that
 * carries our logo where theirs should be is a proposal they will not send.
 */
export function brandLetter(name: string): string {
  const letter = name.trim().charAt(0)
  return letter ? letter.toUpperCase() : '•'
}
